import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { footerContacts, useCaseSite } from '../../app/caseSite';
import { useSite } from '../../app/site';
import { ErrorBlock } from '../../components/ErrorBlock';
import { SendIcon } from '../../components/Icons';
import { useI18n, useT } from '../../i18n';
import { formatTime } from '../../lib/format';
import { isMine, useChat } from './useChat';

export function ChatSection({ caseId }: { caseId: string }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  // радимо зателефонувати лише якщо телефон вказано; сайт справи (напр. MD) має пріоритет над сайтом кабінету
  const caseSite = useCaseSite((s) => s.site);
  const site = useSite((s) => s.site);
  const phone = footerContacts(caseSite, site).phone;
  const { messages, loaded, offline, error, send, resend, refresh } = useChat(caseId);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const count = messages.length;

  // keep the newest message in view unless the person scrolled up to read
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [count]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    stick.current = true;
    setDraft('');
    void send(text);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends on desktop; on phones the on-screen Enter inserts a newline (people often write long messages)
    if (e.key === 'Enter' && !e.shiftKey && matchMedia('(pointer: fine)').matches) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <section aria-labelledby="chat-h" id="chat" className="scroll-mt-6">
      <h2 id="chat-h" className="sec-h">
        {t('chat.title')}
      </h2>
      <p className="mt-4 text-[15px] leading-relaxed text-txt">
        {t('chat.intro')}
        {phone ? (
          <>
            {' '}
            {t('chat.urgentCall')}{' '}
            <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="whitespace-nowrap underline decoration-line underline-offset-4 hover:decoration-fg">
              {phone}
            </a>
            .
          </>
        ) : null}
      </p>

      {error && !count ? <ErrorBlock error={error} onRetry={() => void refresh()} /> : null}
      {offline ? (
        <p role="status" className="mt-4 text-[14px] text-lab">
          {t('chat.offline')}
        </p>
      ) : null}

      <div ref={listRef} className="mt-5 max-h-[60vh] overflow-y-auto overscroll-contain border-y border-line py-4" role="log" aria-live="polite" aria-label={t('chat.title')} tabIndex={0}>
        {loaded && !count ? <p className="py-6 text-center text-[15px] text-lab">{t('chat.empty')}</p> : null}
        {!loaded ? <p className="py-6 text-center text-[15px] text-lab">{t('app.loading')}</p> : null}
        <ul className="flex flex-col gap-3">
          {messages.map((m) => {
            const mine = isMine(m);
            return (
              <li key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap break-words px-4 py-3 text-[16px] leading-relaxed ${mine ? 'bg-fg text-bg' : 'border border-line'}`}>
                  {m.body}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[12px] text-lab">
                  <span>{mine ? t('chat.you') : m.author_name || t('chat.manager')}</span>
                  <span aria-hidden>·</span>
                  {m.local === 'sending' ? <span>{t('chat.sending')}</span> : null}
                  {m.local === 'failed' ? (
                    <span role="alert" className="text-bad">
                      {t('chat.failed')}{' '}
                      <button type="button" className="min-h-tap px-1 text-fg underline underline-offset-4" onClick={() => void resend(m.id)}>
                        {t('chat.retry')}
                      </button>
                    </span>
                  ) : null}
                  {!m.local ? <time dateTime={m.sent_at}>{formatTime(m.sent_at, locale)}</time> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <form onSubmit={submit} className="mt-4 flex items-end gap-3">
        <label htmlFor="chat-input" className="sr-only">
          {t('chat.label')}
        </label>
        <textarea
          id="chat-input"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={t('chat.placeholder')}
          className="field-input min-h-[52px] flex-1 resize-none text-[16px] leading-snug"
          maxLength={4000}
        />
        <button type="submit" className="btn-solid h-[52px] min-h-[52px] w-[52px] flex-none px-0 sm:w-auto sm:px-6" disabled={!draft.trim()}>
          <SendIcon />
          <span className="sr-only sm:not-sr-only">{t('chat.send')}</span>
        </button>
      </form>
    </section>
  );
}
